import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { GripVertical, LayoutGrid, Plus, RotateCcw, X } from 'lucide-react';
import { Button } from './ui/button';
import { Modal } from './ui/modal';
import { toast } from 'sonner';
import {
  createDashboardPreset,
  deleteDashboardPreset,
  getDashboardProfile,
  getUserProfile,
  listDashboardPresets,
  updateDashboardProfile,
  updateDashboardPreset,
} from '../api/api';
import EquityCurveWidget from './dashboard-widgets/EquityCurveWidget';
import AICreditsWidget from './dashboard-widgets/AICreditsWidget';
import AISystemWidget from './dashboard-widgets/AISystemWidget';
import ActiveBotsWidget from './dashboard-widgets/ActiveBotsWidget';
import AIExecutionsWidget from './dashboard-widgets/AIExecutionsWidget';
import JournalWidget from './dashboard-widgets/JournalWidget';
import PortfolioWidget from './dashboard-widgets/PortfolioWidget';
import AccountsWidget from './dashboard-widgets/AccountsWidget';
import TradeHistoryWidget from './dashboard-widgets/TradeHistoryWidget';
import NewsWidget from './dashboard-widgets/NewsWidget';
import DashboardShortcutsWidget from './dashboard-widgets/DashboardShortcutsWidget';

const ResponsiveGridLayout = WidthProvider(Responsive);

const STORAGE_KEY = 'valpha.dashboard.widget-canvas.v5';
const ACTIVE_PRESET_KEY = 'valpha.dashboard.widget-canvas.active-preset.v5';
const BREAKPOINTS = { lg: 1200, md: 996, sm: 768, xs: 0 };
const COLS = { lg: 12, md: 10, sm: 6, xs: 4 };
const ROW_HEIGHT = 32;
const REQUIRED_WIDGET_IDS = ['needsReview', 'balance', 'openPositions', 'pendingOrders'];
const DEFAULT_WIDGET_IDS = [
  ...REQUIRED_WIDGET_IDS,
  'equityCurve',
  'aiCredits',
  'activeBots',
  'portfolio',
  'aiSystem',
  'aiExecutions',
  'journal',
  'shortcuts',
];

const DEFAULT_LAYOUTS = {
  lg: [
    { i: 'needsReview', x: 0, y: 0, w: 3, h: 4 },
    { i: 'balance', x: 3, y: 0, w: 9, h: 4 },
    { i: 'openPositions', x: 0, y: 4, w: 12, h: 8 },
    { i: 'pendingOrders', x: 0, y: 12, w: 12, h: 6 },
    { i: 'equityCurve', x: 0, y: 18, w: 8, h: 9 },
    { i: 'aiCredits', x: 8, y: 18, w: 4, h: 4 },
    { i: 'activeBots', x: 8, y: 22, w: 4, h: 5 },
    { i: 'portfolio', x: 0, y: 27, w: 12, h: 10 },
    { i: 'aiSystem', x: 0, y: 37, w: 4, h: 8 },
    { i: 'aiExecutions', x: 4, y: 37, w: 4, h: 8 },
    { i: 'journal', x: 8, y: 37, w: 4, h: 8 },
    { i: 'shortcuts', x: 0, y: 45, w: 6, h: 5 },
  ],
  md: [
    { i: 'needsReview', x: 0, y: 0, w: 4, h: 4 },
    { i: 'balance', x: 4, y: 0, w: 6, h: 4 },
    { i: 'openPositions', x: 0, y: 4, w: 10, h: 8 },
    { i: 'pendingOrders', x: 0, y: 12, w: 10, h: 6 },
    { i: 'equityCurve', x: 0, y: 18, w: 10, h: 8 },
    { i: 'aiCredits', x: 0, y: 26, w: 5, h: 4 },
    { i: 'activeBots', x: 5, y: 26, w: 5, h: 4 },
    { i: 'portfolio', x: 0, y: 30, w: 10, h: 10 },
    { i: 'aiSystem', x: 0, y: 40, w: 5, h: 8 },
    { i: 'aiExecutions', x: 5, y: 40, w: 5, h: 8 },
    { i: 'journal', x: 0, y: 48, w: 10, h: 8 },
    { i: 'shortcuts', x: 0, y: 56, w: 10, h: 5 },
  ],
  sm: [
    { i: 'needsReview', x: 0, y: 0, w: 6, h: 4 },
    { i: 'balance', x: 0, y: 4, w: 6, h: 4 },
    { i: 'openPositions', x: 0, y: 8, w: 6, h: 8 },
    { i: 'pendingOrders', x: 0, y: 16, w: 6, h: 6 },
    { i: 'equityCurve', x: 0, y: 22, w: 6, h: 8 },
    { i: 'aiCredits', x: 0, y: 30, w: 3, h: 4 },
    { i: 'activeBots', x: 3, y: 30, w: 3, h: 4 },
    { i: 'portfolio', x: 0, y: 34, w: 6, h: 10 },
    { i: 'aiSystem', x: 0, y: 44, w: 6, h: 7 },
    { i: 'aiExecutions', x: 0, y: 51, w: 6, h: 7 },
    { i: 'journal', x: 0, y: 58, w: 6, h: 8 },
    { i: 'shortcuts', x: 0, y: 66, w: 6, h: 5 },
  ],
  xs: [
    { i: 'needsReview', x: 0, y: 0, w: 4, h: 4 },
    { i: 'balance', x: 0, y: 4, w: 4, h: 5 },
    { i: 'openPositions', x: 0, y: 9, w: 4, h: 8 },
    { i: 'pendingOrders', x: 0, y: 17, w: 4, h: 6 },
    { i: 'equityCurve', x: 0, y: 23, w: 4, h: 8 },
    { i: 'aiCredits', x: 0, y: 31, w: 4, h: 4 },
    { i: 'activeBots', x: 0, y: 35, w: 4, h: 4 },
    { i: 'portfolio', x: 0, y: 39, w: 4, h: 10 },
    { i: 'aiSystem', x: 0, y: 49, w: 4, h: 8 },
    { i: 'aiExecutions', x: 0, y: 57, w: 4, h: 8 },
    { i: 'journal', x: 0, y: 65, w: 4, h: 8 },
    { i: 'shortcuts', x: 0, y: 73, w: 4, h: 5 },
  ],
};

const WIDGET_META = {
  needsReview: {
    label: 'Needs Review',
    description: 'Pending approvals and important alerts.',
    minW: 3,
    minH: 3,
    sizes: {
      lg: { w: 3, h: 4 },
      md: { w: 4, h: 4 },
      sm: { w: 6, h: 4 },
      xs: { w: 4, h: 4 },
    },
  },
  balance: {
    label: 'Balance',
    description: 'Account balance, equity, P/L, and exposure.',
    minW: 4,
    minH: 3,
    sizes: {
      lg: { w: 9, h: 4 },
      md: { w: 6, h: 4 },
      sm: { w: 6, h: 4 },
      xs: { w: 4, h: 5 },
    },
  },
  openPositions: {
    label: 'Open Positions',
    description: 'Live positions and trade-management actions.',
    minW: 4,
    minH: 6,
    sizes: {
      lg: { w: 12, h: 8 },
      md: { w: 10, h: 8 },
      sm: { w: 6, h: 8 },
      xs: { w: 4, h: 8 },
    },
  },
  pendingOrders: {
    label: 'Pending Orders',
    description: 'Orders waiting to trigger.',
    minW: 4,
    minH: 5,
    sizes: {
      lg: { w: 12, h: 6 },
      md: { w: 10, h: 6 },
      sm: { w: 6, h: 6 },
      xs: { w: 4, h: 6 },
    },
  },
  equityCurve: {
    label: 'Equity Curve',
    description: 'Track the selected account equity over time.',
    minW: 4,
    minH: 5,
    sizes: {
      lg: { w: 8, h: 9 },
      md: { w: 10, h: 8 },
      sm: { w: 6, h: 8 },
      xs: { w: 4, h: 8 },
    },
  },
  aiCredits: {
    label: 'Credits',
    description: 'Monitor credit balance and recent usage.',
    minW: 3,
    minH: 4,
    sizes: {
      lg: { w: 4, h: 4 },
      md: { w: 5, h: 4 },
      sm: { w: 3, h: 4 },
      xs: { w: 4, h: 4 },
    },
  },
  activeBots: {
    label: 'Trading Bots',
    description: 'See which bots are active and how they are behaving.',
    minW: 3,
    minH: 4,
    sizes: {
      lg: { w: 4, h: 5 },
      md: { w: 5, h: 4 },
      sm: { w: 3, h: 4 },
      xs: { w: 4, h: 4 },
    },
  },
  portfolio: {
    label: 'Portfolio Analytics',
    description: 'Aggregate exposure, symbol mix, and floating P/L.',
    minW: 4,
    minH: 6,
    sizes: {
      lg: { w: 12, h: 10 },
      md: { w: 10, h: 10 },
      sm: { w: 6, h: 10 },
      xs: { w: 4, h: 10 },
    },
  },
  aiSystem: {
    label: 'AI Systems',
    description: 'Review active systems and schedule status.',
    minW: 4,
    minH: 6,
    sizes: {
      lg: { w: 4, h: 8 },
      md: { w: 5, h: 8 },
      sm: { w: 6, h: 7 },
      xs: { w: 4, h: 8 },
    },
  },
  aiExecutions: {
    label: 'AI Executions',
    description: 'Watch recent AI jobs and execution outcomes.',
    minW: 4,
    minH: 6,
    sizes: {
      lg: { w: 4, h: 8 },
      md: { w: 5, h: 8 },
      sm: { w: 6, h: 7 },
      xs: { w: 4, h: 8 },
    },
  },
  journal: {
    label: 'Trade Journal',
    description: 'Latest notes, emotions, and execution reasoning.',
    minW: 4,
    minH: 6,
    sizes: {
      lg: { w: 4, h: 8 },
      md: { w: 10, h: 8 },
      sm: { w: 6, h: 8 },
      xs: { w: 4, h: 8 },
    },
  },
  accounts: {
    label: 'Accounts',
    description: 'Overview of connected trading accounts.',
    minW: 4,
    minH: 5,
    sizes: {
      lg: { w: 6, h: 8 },
      md: { w: 5, h: 8 },
      sm: { w: 6, h: 8 },
      xs: { w: 4, h: 8 },
    },
  },
  tradeHistory: {
    label: 'Trade History',
    description: 'Closed trades, metrics, and filterable performance data.',
    minW: 5,
    minH: 7,
    sizes: {
      lg: { w: 12, h: 11 },
      md: { w: 10, h: 10 },
      sm: { w: 6, h: 10 },
      xs: { w: 4, h: 10 },
    },
  },
  news: {
    label: 'News',
    description: 'Latest market headlines and summaries.',
    minW: 4,
    minH: 5,
    sizes: {
      lg: { w: 6, h: 8 },
      md: { w: 5, h: 8 },
      sm: { w: 6, h: 8 },
      xs: { w: 4, h: 8 },
    },
  },
  shortcuts: {
    label: 'Shortcuts',
    description: 'Keep your most-used dashboard exits close by.',
    minW: 4,
    minH: 4,
    sizes: {
      lg: { w: 6, h: 5 },
      md: { w: 10, h: 5 },
      sm: { w: 6, h: 5 },
      xs: { w: 4, h: 5 },
    },
  },
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const ensureRequiredWidgets = (widgets = []) => {
  const validWidgets = Array.isArray(widgets)
    ? widgets.filter((widgetId) => WIDGET_META[widgetId])
    : [];
  const seen = new Set();

  return [...REQUIRED_WIDGET_IDS, ...validWidgets].filter((widgetId) => {
    if (!WIDGET_META[widgetId] || seen.has(widgetId)) {
      return false;
    }
    seen.add(widgetId);
    return true;
  });
};

const getWidgetSize = (widgetId, breakpoint) => {
  const meta = WIDGET_META[widgetId];
  const size = meta?.sizes?.[breakpoint] || meta?.sizes?.lg || { w: 4, h: 4 };
  return {
    w: Math.min(size.w, COLS[breakpoint]),
    h: size.h,
  };
};

const sanitizeLayoutItem = (widgetId, item, breakpoint) => {
  const meta = WIDGET_META[widgetId];
  const cols = COLS[breakpoint];
  const size = getWidgetSize(widgetId, breakpoint);
  const minW = Math.min(meta?.minW || 2, cols);
  const minH = meta?.minH || 2;
  const w = clamp(Number.isFinite(item?.w) ? Math.round(item.w) : size.w, minW, cols);
  const h = Math.max(Number.isFinite(item?.h) ? Math.round(item.h) : size.h, minH);
  const x = clamp(Number.isFinite(item?.x) ? Math.round(item.x) : 0, 0, Math.max(cols - w, 0));
  const y = Math.max(Number.isFinite(item?.y) ? Math.round(item.y) : 0, 0);

  return {
    i: widgetId,
    x,
    y,
    w,
    h,
    minW,
    minH,
  };
};

const buildDefaultLayoutItem = (widgetId, breakpoint, existingItems = []) => {
  const template = DEFAULT_LAYOUTS[breakpoint]?.find((item) => item.i === widgetId);
  if (template) {
    return sanitizeLayoutItem(widgetId, template, breakpoint);
  }

  const size = getWidgetSize(widgetId, breakpoint);
  const y = existingItems.reduce((bottom, item) => Math.max(bottom, item.y + item.h), 0);

  return sanitizeLayoutItem(widgetId, { i: widgetId, x: 0, y, w: size.w, h: size.h }, breakpoint);
};

const buildStackedLayoutFromSource = (activeWidgets, breakpoint, sourceItems = []) => {
  const sourceById = new Map(sourceItems.map((item) => [item.i, item]));
  const activeSet = new Set(activeWidgets);
  const seen = new Set();
  const orderedWidgetIds = [
    ...sourceItems
      .filter((item) => activeSet.has(item.i))
      .sort((a, b) => (a.y - b.y) || (a.x - b.x))
      .map((item) => item.i),
    ...activeWidgets,
  ].filter((widgetId) => {
    if (!WIDGET_META[widgetId] || seen.has(widgetId)) {
      return false;
    }
    seen.add(widgetId);
    return true;
  });

  let y = 0;

  return orderedWidgetIds.map((widgetId) => {
    const sourceItem = sourceById.get(widgetId);
    const defaultSize = getWidgetSize(widgetId, breakpoint);
    const sourceHeight = Number.isFinite(sourceItem?.h) ? Math.round(sourceItem.h) : defaultSize.h;
    const item = sanitizeLayoutItem(
      widgetId,
      {
        ...sourceItem,
        i: widgetId,
        x: 0,
        y,
        w: COLS[breakpoint],
        h: Math.max(sourceHeight, defaultSize.h),
      },
      breakpoint
    );
    y += item.h;
    return item;
  });
};

const normalizeLayouts = (activeWidgets, incomingLayouts = {}) => {
  const normalized = {};

  Object.keys(COLS).forEach((breakpoint) => {
    if (breakpoint === 'xs') {
      normalized[breakpoint] = buildStackedLayoutFromSource(
        activeWidgets,
        breakpoint,
        normalized.lg || normalized.md || normalized.sm || incomingLayouts[breakpoint] || []
      );
      return;
    }

    const sourceItems = Array.isArray(incomingLayouts[breakpoint]) ? incomingLayouts[breakpoint] : [];
    const sourceById = new Map(sourceItems.map((item) => [item.i, item]));
    const hasRequiredLayout = REQUIRED_WIDGET_IDS.every((widgetId) => sourceById.has(widgetId));
    const nextItems = [];

    activeWidgets.forEach((widgetId) => {
      if (!WIDGET_META[widgetId]) {
        return;
      }

      const existing = hasRequiredLayout ? sourceById.get(widgetId) : null;
      nextItems.push(
        existing
          ? sanitizeLayoutItem(widgetId, existing, breakpoint)
          : buildDefaultLayoutItem(widgetId, breakpoint, nextItems)
      );
    });

    normalized[breakpoint] = nextItems;
  });

  return normalized;
};

const buildWorkspaceState = (activeWidgets, layouts) => ({
  version: 1,
  activeWidgets,
  layouts,
});

const getInitialSelectedPresetId = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  try {
    return window.localStorage.getItem(ACTIVE_PRESET_KEY) || '';
  } catch {
    return '';
  }
};

const getStoredIsStaffUser = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const stored = window.localStorage.getItem('user');
    return stored ? JSON.parse(stored)?.is_staff === true : false;
  } catch {
    return false;
  }
};

const getApiErrorMessage = (error, fallback) => {
  if (typeof error === 'string') {
    return error;
  }

  if (Array.isArray(error) && error.length > 0) {
    return error[0];
  }

  if (error?.detail) {
    return error.detail;
  }

  if (error?.message) {
    return error.message;
  }

  if (error && typeof error === 'object') {
    const firstValue = Object.values(error)[0];
    if (Array.isArray(firstValue) && firstValue.length > 0) {
      return firstValue[0];
    }
    if (typeof firstValue === 'string') {
      return firstValue;
    }
  }

  return fallback;
};

const normalizePresetRecord = (preset) => {
  const filteredWidgets = Array.isArray(preset?.active_widgets)
    ? preset.active_widgets.filter((widgetId) => WIDGET_META[widgetId])
    : DEFAULT_WIDGET_IDS;
  const activeWidgets = ensureRequiredWidgets(filteredWidgets.length ? filteredWidgets : DEFAULT_WIDGET_IDS);

  return {
    ...preset,
    active_widgets: activeWidgets,
    layouts: normalizeLayouts(activeWidgets, preset?.layouts || DEFAULT_LAYOUTS),
  };
};

const normalizeProfileRecord = (profile) => {
  const filteredWidgets = Array.isArray(profile?.active_widgets)
    ? profile.active_widgets.filter((widgetId) => WIDGET_META[widgetId])
    : [];

  if (filteredWidgets.length === 0) {
    return null;
  }

  const activeWidgets = ensureRequiredWidgets(filteredWidgets);

  return {
    activeWidgets,
    layouts: normalizeLayouts(activeWidgets, profile?.layouts || DEFAULT_LAYOUTS),
    selectedPresetId: profile?.selected_preset_id ? String(profile.selected_preset_id) : '',
  };
};

const parsePresetList = (response) => {
  const presets = response?.results || response;
  return Array.isArray(presets) ? presets.map(normalizePresetRecord) : [];
};

const getInitialDashboardState = () => {
  if (typeof window === 'undefined') {
    return {
      activeWidgets: DEFAULT_WIDGET_IDS,
      layouts: normalizeLayouts(DEFAULT_WIDGET_IDS, DEFAULT_LAYOUTS),
    };
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {
        activeWidgets: DEFAULT_WIDGET_IDS,
        layouts: normalizeLayouts(DEFAULT_WIDGET_IDS, DEFAULT_LAYOUTS),
      };
    }

    const parsed = JSON.parse(raw);
    const activeWidgets = Array.isArray(parsed?.activeWidgets)
      ? ensureRequiredWidgets(parsed.activeWidgets.filter((widgetId) => WIDGET_META[widgetId]))
      : DEFAULT_WIDGET_IDS;
    const nextActiveWidgets = activeWidgets.length ? activeWidgets : DEFAULT_WIDGET_IDS;

    return {
      activeWidgets: nextActiveWidgets,
      layouts: normalizeLayouts(nextActiveWidgets, parsed?.layouts || DEFAULT_LAYOUTS),
    };
  } catch {
    return {
      activeWidgets: DEFAULT_WIDGET_IDS,
      layouts: normalizeLayouts(DEFAULT_WIDGET_IDS, DEFAULT_LAYOUTS),
    };
  }
};

function WidgetState({ title, message }) {
  return (
    <div className="flex h-full min-h-[140px] items-center justify-center px-6 py-8 text-center">
      <div>
        <div className="text-sm font-medium text-textPrimary">{title}</div>
        <div className="mt-1 text-xs text-textSecondary">{message}</div>
      </div>
    </div>
  );
}

const DashboardWidgetCanvas = ({
  selectedAccountId,
  showPortfolio = false,
  openPositions = [],
  balance = null,
  equity = null,
  headerContent = null,
  mobileHeaderContent = null,
  renderNeedsReviewWidget,
  renderBalanceWidget,
  renderOpenPositionsWidget,
  renderPendingOrdersWidget,
}) => {
  const initialState = useMemo(() => getInitialDashboardState(), []);
  const [activeWidgets, setActiveWidgets] = useState(initialState.activeWidgets);
  const [layouts, setLayouts] = useState(initialState.layouts);
  const [editing, setEditing] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isSavePresetOpen, setIsSavePresetOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [breakpoint, setBreakpoint] = useState('lg');
  const [presets, setPresets] = useState([]);
  const [selectedPresetId, setSelectedPresetId] = useState(getInitialSelectedPresetId);
  const [presetName, setPresetName] = useState('');
  const [saveAsGlobal, setSaveAsGlobal] = useState(false);
  const [presetsLoading, setPresetsLoading] = useState(false);
  const [presetsLoaded, setPresetsLoaded] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [presetBusy, setPresetBusy] = useState(false);
  const [isLayoutControlsOpen, setIsLayoutControlsOpen] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const lastSavedProfileKeyRef = useRef('');
  const profileSaveErrorShownRef = useRef(false);

  const isStaff = useMemo(() => getStoredIsStaffUser() || userProfile?.is_staff === true, [userProfile]);

  const isCompactScreen = breakpoint === 'xs';

  const selectedPreset = useMemo(
    () => presets.find((preset) => String(preset.id) === String(selectedPresetId)) || null,
    [presets, selectedPresetId]
  );

  const canManageSelectedPreset = Boolean(selectedPreset) && (!selectedPreset.is_global || isStaff);
  const compactHeaderContent = mobileHeaderContent ?? headerContent;

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(buildWorkspaceState(activeWidgets, layouts)));
  }, [activeWidgets, layouts]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      if (selectedPresetId) {
        window.localStorage.setItem(ACTIVE_PRESET_KEY, String(selectedPresetId));
      } else {
        window.localStorage.removeItem(ACTIVE_PRESET_KEY);
      }
    } catch {
      // Ignore local persistence errors.
    }
  }, [selectedPresetId]);

  useEffect(() => {
    let cancelled = false;

    const loadDashboardProfile = async () => {
      try {
        const profile = await getDashboardProfile();
        if (cancelled) {
          return;
        }

        const normalizedProfile = normalizeProfileRecord(profile);
        if (normalizedProfile) {
          setActiveWidgets(normalizedProfile.activeWidgets);
          setLayouts(normalizedProfile.layouts);
          setSelectedPresetId(normalizedProfile.selectedPresetId);
          lastSavedProfileKeyRef.current = JSON.stringify({
            active_widgets: normalizedProfile.activeWidgets,
            layouts: normalizedProfile.layouts,
            selected_preset_id: normalizedProfile.selectedPresetId
              ? Number(normalizedProfile.selectedPresetId)
              : null,
          });
        }
      } catch (error) {
        console.warn('Failed to load dashboard profile from the database.', error);
        toast.error('Dashboard layout could not be loaded from your account.');
      } finally {
        if (!cancelled) {
          setProfileLoaded(true);
        }
      }
    };

    loadDashboardProfile();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!profileLoaded) {
      return undefined;
    }

    const selectedPresetNumericId = presetsLoaded && selectedPreset ? Number(selectedPreset.id) : null;
    const profilePayload = {
      active_widgets: activeWidgets,
      layouts,
      selected_preset_id: Number.isFinite(selectedPresetNumericId) ? selectedPresetNumericId : null,
    };
    const profileKey = JSON.stringify(profilePayload);

    if (lastSavedProfileKeyRef.current === profileKey) {
      return undefined;
    }

    const saveTimer = window.setTimeout(() => {
      updateDashboardProfile(profilePayload)
        .then(() => {
          lastSavedProfileKeyRef.current = profileKey;
          profileSaveErrorShownRef.current = false;
        })
        .catch((error) => {
          console.warn('Failed to save dashboard profile to the database.', error);
          if (!profileSaveErrorShownRef.current) {
            toast.error('Dashboard layout could not be saved to your account.');
            profileSaveErrorShownRef.current = true;
          }
        });
    }, 700);

    return () => window.clearTimeout(saveTimer);
  }, [activeWidgets, layouts, presetsLoaded, profileLoaded, selectedPreset]);

  useEffect(() => {
    let cancelled = false;

    const loadUserProfile = () => {
      getUserProfile()
        .then((profile) => {
          if (!cancelled) {
            setUserProfile(profile || null);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setUserProfile(null);
          }
        });
    };

    loadUserProfile();
    window.addEventListener('valpha:profile-updated', loadUserProfile);

    return () => {
      cancelled = true;
      window.removeEventListener('valpha:profile-updated', loadUserProfile);
    };
  }, []);

  const refreshPresets = useCallback(async (preferredSelectedId = null) => {
    setPresetsLoading(true);
    setPresetsLoaded(false);
    try {
      const response = await listDashboardPresets();
      const nextPresets = parsePresetList(response);
      setPresets(nextPresets);
      setPresetsLoaded(true);
      setSelectedPresetId((currentId) => {
        const candidateId = preferredSelectedId ?? currentId;
        if (!candidateId) {
          return '';
        }
        return nextPresets.some((preset) => String(preset.id) === String(candidateId))
          ? String(candidateId)
          : '';
      });
      return nextPresets;
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load dashboard presets.'));
      return [];
    } finally {
      setPresetsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshPresets();
  }, [refreshPresets]);

  const widgetRegistry = useMemo(
    () => ({
      needsReview: {
        ...WIDGET_META.needsReview,
        render: () => (
          typeof renderNeedsReviewWidget === 'function'
            ? renderNeedsReviewWidget()
            : <WidgetState title="Needs Review unavailable" message="This widget could not be loaded." />
        ),
      },
      balance: {
        ...WIDGET_META.balance,
        render: () => (
          typeof renderBalanceWidget === 'function'
            ? renderBalanceWidget()
            : <WidgetState title="Balance unavailable" message="This widget could not be loaded." />
        ),
      },
      openPositions: {
        ...WIDGET_META.openPositions,
        render: () => (
          typeof renderOpenPositionsWidget === 'function'
            ? renderOpenPositionsWidget()
            : <WidgetState title="Open Positions unavailable" message="This widget could not be loaded." />
        ),
      },
      pendingOrders: {
        ...WIDGET_META.pendingOrders,
        render: () => (
          typeof renderPendingOrdersWidget === 'function'
            ? renderPendingOrdersWidget()
            : <WidgetState title="Pending Orders unavailable" message="This widget could not be loaded." />
        ),
      },
      equityCurve: {
        ...WIDGET_META.equityCurve,
        render: () => {
          if (showPortfolio) {
            return (
              <WidgetState
                title="Single-account widget"
                message="Switch back to Single mode to view an account equity curve."
              />
            );
          }

          if (!selectedAccountId) {
            return (
              <WidgetState
                title="Select an account"
                message="Choose an account above to load the equity curve."
              />
            );
          }

          return <EquityCurveWidget accountId={selectedAccountId} hideTitle />;
        },
      },
      aiCredits: {
        ...WIDGET_META.aiCredits,
        render: () => <AICreditsWidget hideTitle />,
      },
      activeBots: {
        ...WIDGET_META.activeBots,
        render: () => <ActiveBotsWidget hideTitle />,
      },
      portfolio: {
        ...WIDGET_META.portfolio,
        render: () => <PortfolioWidget positions={openPositions} balance={balance} equity={equity} hideTitle />,
      },
      aiSystem: {
        ...WIDGET_META.aiSystem,
        render: () => <AISystemWidget hideTitle />,
      },
      aiExecutions: {
        ...WIDGET_META.aiExecutions,
        render: () => <AIExecutionsWidget hideTitle />,
      },
      journal: {
        ...WIDGET_META.journal,
        render: () => <JournalWidget accountId={selectedAccountId || null} hideTitle limit={8} />,
      },
      accounts: {
        ...WIDGET_META.accounts,
        render: () => <AccountsWidget hideTitle compact />,
      },
      tradeHistory: {
        ...WIDGET_META.tradeHistory,
        render: () => {
          if (showPortfolio) {
            return (
              <WidgetState
                title="Single-account widget"
                message="Trade history uses the selected account context in Single mode."
              />
            );
          }

          if (!selectedAccountId) {
            return (
              <WidgetState
                title="Select an account"
                message="Choose an account above to load its trade history."
              />
            );
          }

          return <TradeHistoryWidget accountId={selectedAccountId} hideTitle compact />;
        },
      },
      news: {
        ...WIDGET_META.news,
        render: () => <NewsWidget hideTitle limit={12} autoRefreshMs={60000} />,
      },
      shortcuts: {
        ...WIDGET_META.shortcuts,
        render: () => <DashboardShortcutsWidget hideTitle />,
      },
    }),
    [
      showPortfolio,
      selectedAccountId,
      openPositions,
      balance,
      equity,
      renderNeedsReviewWidget,
      renderBalanceWidget,
      renderOpenPositionsWidget,
      renderPendingOrdersWidget,
    ]
  );

  const availableWidgets = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return Object.entries(widgetRegistry)
      .filter(([widgetId, widget]) => {
        if (REQUIRED_WIDGET_IDS.includes(widgetId)) {
          return false;
        }
        if (!query) {
          return true;
        }
        return `${widget.label} ${widget.description}`.toLowerCase().includes(query);
      })
      .sort((a, b) => a[1].label.localeCompare(b[1].label));
  }, [widgetRegistry, searchQuery]);

  const handleLayoutChange = (_currentLayout, allLayouts) => {
    setLayouts((previousLayouts) => normalizeLayouts(activeWidgets, { ...previousLayouts, ...allLayouts }));
  };

  const openSavePresetModal = () => {
    setPresetName(selectedPreset ? `${selectedPreset.name} copy` : '');
    setSaveAsGlobal(isStaff && Boolean(selectedPreset?.is_global));
    setIsSavePresetOpen(true);
  };

  const closeSavePresetModal = () => {
    setIsSavePresetOpen(false);
    setPresetName('');
    setSaveAsGlobal(false);
  };

  const applyPreset = (preset) => {
    if (!preset) {
      return;
    }

    setActiveWidgets(preset.active_widgets);
    setLayouts(preset.layouts);
    setSelectedPresetId(String(preset.id));
    toast.success(`Loaded preset "${preset.name}".`);
  };

  const handleCreatePreset = async (event) => {
    event.preventDefault();
    const trimmedName = presetName.trim();
    if (!trimmedName) {
      return;
    }

    setPresetBusy(true);
    try {
      const created = await createDashboardPreset({
        name: trimmedName,
        active_widgets: activeWidgets,
        layouts,
        is_global: isStaff ? saveAsGlobal : false,
      });
      const createdId = String(created?.id || '');
      await refreshPresets(createdId);
      if (createdId) {
        setSelectedPresetId(createdId);
      }
      closeSavePresetModal();
      toast.success(saveAsGlobal ? 'Global dashboard preset saved.' : 'Dashboard preset saved.');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to save dashboard preset.'));
    } finally {
      setPresetBusy(false);
    }
  };

  const handleUpdatePreset = async () => {
    if (!selectedPreset || !canManageSelectedPreset) {
      return;
    }

    setPresetBusy(true);
    try {
      await updateDashboardPreset(selectedPreset.id, {
        name: selectedPreset.name,
        active_widgets: activeWidgets,
        layouts,
        is_global: selectedPreset.is_global,
      });
      await refreshPresets(selectedPreset.id);
      toast.success(`Updated preset "${selectedPreset.name}".`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update dashboard preset.'));
    } finally {
      setPresetBusy(false);
    }
  };

  const handleDeletePreset = async () => {
    if (!selectedPreset || !canManageSelectedPreset) {
      return;
    }

    const shouldDelete = window.confirm(`Delete preset "${selectedPreset.name}"?`);
    if (!shouldDelete) {
      return;
    }

    setPresetBusy(true);
    try {
      await deleteDashboardPreset(selectedPreset.id);
      await refreshPresets('');
      setSelectedPresetId('');
      toast.success(`Deleted preset "${selectedPreset.name}".`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to delete dashboard preset.'));
    } finally {
      setPresetBusy(false);
    }
  };

  const addWidget = (widgetId) => {
    if (activeWidgets.includes(widgetId) || !widgetRegistry[widgetId]) {
      return;
    }

    const nextActiveWidgets = [...activeWidgets, widgetId];
    setActiveWidgets(nextActiveWidgets);
    setLayouts((previousLayouts) => normalizeLayouts(nextActiveWidgets, previousLayouts));
    setEditing(true);
    setIsPickerOpen(false);
  };

  const removeWidget = (widgetId) => {
    if (REQUIRED_WIDGET_IDS.includes(widgetId)) {
      return;
    }
    const nextActiveWidgets = activeWidgets.filter((activeWidgetId) => activeWidgetId !== widgetId);
    setActiveWidgets(nextActiveWidgets);
    setLayouts((previousLayouts) => normalizeLayouts(nextActiveWidgets, previousLayouts));
  };

  const resetLayout = () => {
    setActiveWidgets(DEFAULT_WIDGET_IDS);
    setLayouts(normalizeLayouts(DEFAULT_WIDGET_IDS, DEFAULT_LAYOUTS));
  };

  const fitLayout = () => {
    const fittedWidgets = ensureRequiredWidgets(activeWidgets.length ? activeWidgets : DEFAULT_WIDGET_IDS);
    setActiveWidgets(fittedWidgets);
    setLayouts(normalizeLayouts(fittedWidgets, DEFAULT_LAYOUTS));
  };

  const renderWorkspaceActions = () => (
    <div className="flex max-w-full flex-wrap items-center gap-2 rounded-lg border border-borderDefault bg-backgroundBody/45 p-1.5">
      <Button
        type="button"
        size="sm"
        variant={editing ? 'secondary' : 'outline'}
        className="whitespace-nowrap"
        onClick={() => setEditing((currentValue) => !currentValue)}
      >
        {editing ? 'Done' : 'Customize'}
      </Button>
      <Button type="button" size="sm" variant="outline" className="whitespace-nowrap" onClick={fitLayout}>
        <LayoutGrid className="mr-1 h-4 w-4" />
        Auto Fit
      </Button>
      <Button type="button" size="sm" className="whitespace-nowrap" onClick={() => setIsPickerOpen(true)}>
        <Plus className="mr-1 h-4 w-4" />
        Add Widget
      </Button>
      <Button type="button" size="sm" variant="outline" className="whitespace-nowrap" onClick={openSavePresetModal}>
        Save Preset
      </Button>
      <Button type="button" size="sm" variant="ghost" className="whitespace-nowrap" onClick={resetLayout}>
        <RotateCcw className="mr-1 h-4 w-4" />
        Reset
      </Button>
    </div>
  );

  const renderWorkspacePresets = () => (
    <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2 rounded-lg border border-borderDefault bg-backgroundBody/45 p-1.5">
      <span className="px-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-textSecondary">
        Presets
      </span>
      <select
        value={selectedPresetId}
        onChange={(event) => setSelectedPresetId(event.target.value)}
        className="min-w-[160px] max-w-full flex-[1_1_180px] rounded-lg border border-borderDefault bg-backgroundSurface px-3 py-2 text-sm text-textPrimary outline-none transition focus:border-primary sm:min-w-[220px]"
        disabled={presetsLoading}
      >
        <option value="">Current workspace</option>
        {presets.map((preset) => (
          <option key={preset.id} value={preset.id}>
            {preset.is_global ? `Global \u00b7 ${preset.name}` : preset.name}
          </option>
        ))}
      </select>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="shrink-0 whitespace-nowrap"
        onClick={() => applyPreset(selectedPreset)}
        disabled={!selectedPreset || presetBusy || presetsLoading}
      >
        Load
      </Button>
      {canManageSelectedPreset && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="shrink-0 whitespace-nowrap"
          onClick={handleUpdatePreset}
          disabled={!selectedPreset || presetBusy}
        >
          Save
        </Button>
      )}
      {canManageSelectedPreset && (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="shrink-0 whitespace-nowrap"
          onClick={handleDeletePreset}
          disabled={!selectedPreset || presetBusy}
        >
          Delete
        </Button>
      )}
    </div>
  );

  const renderLayoutToggleButton = (compact = false) => (
    <Button
      type="button"
      size="sm"
      variant={isLayoutControlsOpen ? 'secondary' : 'outline'}
      className={compact ? 'h-9 shrink-0 whitespace-nowrap px-2.5' : 'shrink-0 whitespace-nowrap'}
      onClick={() => setIsLayoutControlsOpen((currentValue) => !currentValue)}
      aria-label="Layout controls"
      aria-expanded={isLayoutControlsOpen}
      aria-controls={compact ? 'dashboard-layout-controls-mobile' : 'dashboard-layout-controls-desktop'}
      title="Layout controls"
    >
      <LayoutGrid className="mr-1 h-4 w-4" />
      Layout
    </Button>
  );

  return (
    <div className="space-y-4">
      <section className="sticky top-0 z-30 w-full rounded-lg border border-borderDefault bg-backgroundSurface/95 px-2 py-2 shadow-md backdrop-blur sm:px-3">
        <div className="hidden min-w-0 flex-wrap items-center gap-2 md:flex">
          <div className="hidden min-w-0 flex-1 md:block">
            {headerContent}
          </div>

          <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2">
            {isLayoutControlsOpen && (
              <div id="dashboard-layout-controls-desktop" className="flex min-w-0 flex-wrap items-center justify-end gap-2">
                {renderWorkspaceActions()}
                {renderWorkspacePresets()}
              </div>
            )}
            {renderLayoutToggleButton()}
          </div>
        </div>

        <div className="flex items-center gap-2 md:hidden">
          <div className="min-w-0 flex-1">
            {compactHeaderContent}
          </div>
          {renderLayoutToggleButton(true)}
        </div>

        {isLayoutControlsOpen && (
          <div id="dashboard-layout-controls-mobile" className="mt-3 flex flex-col gap-2 md:hidden">
            {renderWorkspaceActions()}
            {renderWorkspacePresets()}
          </div>
        )}
      </section>

      <section className="bg-backgroundSurface p-4 sm:p-6 shadow-md rounded-xl">
      <div className="flex flex-col gap-4">
        {false && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <LayoutGrid className="h-4 w-4 text-primary" />
              <h3 className="text-base sm:text-lg font-medium text-primary">Workspace Settings</h3>
            </div>

            <div className="flex flex-wrap items-center gap-2 xl:flex-nowrap xl:justify-end">
              <span className="rounded-full bg-secondary/15 px-3 py-1 text-xs text-textSecondary shrink-0">
                {activeWidgets.length} widget{activeWidgets.length === 1 ? '' : 's'}
              </span>
              <Button
                type="button"
                size="sm"
                variant={editing ? 'secondary' : 'outline'}
                onClick={() => setEditing((currentValue) => !currentValue)}
              >
                {editing ? 'Done' : 'Customize'}
              </Button>
              <Button type="button" size="sm" onClick={() => setIsPickerOpen(true)}>
                <Plus className="mr-1 h-4 w-4" />
                Add Widget
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={openSavePresetModal}>
                Save Preset
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={resetLayout}>
                <RotateCcw className="mr-1 h-4 w-4" />
                Reset
              </Button>

              <div className="flex min-w-0 flex-wrap items-center gap-2 rounded-lg border border-borderDefault bg-backgroundBody/45 p-1.5 shrink-0">
                <span className="px-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-textSecondary">
                  Presets
                </span>
                <select
                  value={selectedPresetId}
                  onChange={(event) => setSelectedPresetId(event.target.value)}
                  className="min-w-[180px] rounded-lg border border-borderDefault bg-backgroundSurface px-3 py-2 text-sm text-textPrimary outline-none transition focus:border-primary sm:min-w-[220px]"
                  disabled={presetsLoading}
                >
                  <option value="">Current workspace</option>
                  {presets.map((preset) => (
                    <option key={preset.id} value={preset.id}>
                      {preset.is_global ? `Global · ${preset.name}` : preset.name}
                    </option>
                  ))}
                </select>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => applyPreset(selectedPreset)}
                  disabled={!selectedPreset || presetBusy || presetsLoading}
                >
                  Load
                </Button>
                {canManageSelectedPreset && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={handleUpdatePreset}
                    disabled={!selectedPreset || presetBusy}
                  >
                    Save
                  </Button>
                )}
                {canManageSelectedPreset && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={handleDeletePreset}
                    disabled={!selectedPreset || presetBusy}
                  >
                    Delete
                  </Button>
                )}
              </div>
            </div>
          </div>

        </div>
        )}

        {editing && isCompactScreen && (
          <div className="rounded-lg border border-borderDefault bg-backgroundBody/60 px-3 py-2 text-xs text-textSecondary">
            Widgets stack vertically on smaller screens. Add and remove here, then drag and resize on a wider screen.
          </div>
        )}

        {activeWidgets.length === 0 ? (
          <div className="rounded-xl border border-dashed border-borderDefault bg-backgroundBody/40 px-6 py-12 text-center">
            <div className="text-sm font-medium text-textPrimary">No widgets on the workspace</div>
            <div className="mt-1 text-xs sm:text-sm text-textSecondary">
              Add the widgets you want in this dashboard workspace.
            </div>
            <div className="mt-4">
              <Button type="button" size="sm" onClick={() => setIsPickerOpen(true)}>
                <Plus className="mr-1 h-4 w-4" />
                Add Your First Widget
              </Button>
            </div>
          </div>
        ) : (
          <ResponsiveGridLayout
            className="layout"
            layouts={layouts}
            breakpoints={BREAKPOINTS}
            cols={COLS}
            rowHeight={ROW_HEIGHT}
            margin={[16, 16]}
            containerPadding={[0, 0]}
            autoSize
            allowOverlap={false}
            compactType="vertical"
            onLayoutChange={handleLayoutChange}
            onBreakpointChange={(nextBreakpoint) => setBreakpoint(nextBreakpoint)}
            isDraggable={editing && !isCompactScreen}
            isResizable={editing && !isCompactScreen}
            draggableHandle=".dashboard-widget-drag-handle"
            draggableCancel=".dashboard-widget-no-drag,button,a,input,select,textarea,[data-interactive='true']"
          >
            {activeWidgets.map((widgetId) => {
              const widget = widgetRegistry[widgetId];
              if (!widget) {
                return null;
              }
              const isRequiredWidget = REQUIRED_WIDGET_IDS.includes(widgetId);

              return (
                <div key={widgetId} className="h-full min-h-0">
                  <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-borderDefault bg-backgroundSurface shadow-md">
                    <div className="flex items-start justify-between gap-3 border-b border-borderDefault px-3 py-2.5">
                      <div
                        className={`flex min-w-0 items-start gap-2 ${editing && !isCompactScreen ? 'dashboard-widget-drag-handle cursor-grab active:cursor-grabbing' : ''}`}
                        title={editing && !isCompactScreen ? `Drag ${widget.label}` : undefined}
                      >
                        {editing && !isCompactScreen && (
                          <span
                            className="mt-0.5 rounded p-0.5 text-textSecondary transition hover:bg-secondary/10 hover:text-textPrimary"
                            aria-hidden="true"
                          >
                            <GripVertical className="h-4 w-4" />
                          </span>
                        )}
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium text-textPrimary">{widget.label}</div>
                          <div className="mt-0.5 truncate text-[11px] text-textSecondary">{widget.description}</div>
                        </div>
                      </div>

                      {editing && !isRequiredWidget && (
                        <button
                          type="button"
                          className="dashboard-widget-no-drag rounded p-1 text-textSecondary transition hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => removeWidget(widgetId)}
                          aria-label={`Remove ${widget.label}`}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    <div className="min-h-0 flex-1 overflow-auto p-3">
                      {widget.render()}
                    </div>
                  </div>
                </div>
              );
            })}
          </ResponsiveGridLayout>
        )}
      </div>
      </section>

      <Modal
        open={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        title="Add Widget"
        maxWidthClass="max-w-4xl"
      >
        <div className="space-y-4">
          <div>
            <label htmlFor="dashboard-widget-search" className="mb-1 block text-xs font-medium text-textSecondary">
              Search widgets
            </label>
            <input
              id="dashboard-widget-search"
              type="text"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Find a widget"
              className="w-full rounded-lg border border-borderDefault bg-backgroundBody px-3 py-2 text-sm text-textPrimary outline-none transition focus:border-primary"
            />
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {availableWidgets.map(([widgetId, widget]) => {
              const isActive = activeWidgets.includes(widgetId);

              return (
                <div
                  key={widgetId}
                  className="rounded-xl border border-borderDefault bg-backgroundBody/50 p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-medium text-textPrimary">{widget.label}</div>
                      <div className="mt-1 text-xs text-textSecondary">{widget.description}</div>
                    </div>
                    <span
                      className={`rounded-full px-2 py-1 text-[10px] font-medium ${
                        isActive
                          ? 'bg-success/15 text-success'
                          : 'bg-secondary/20 text-textSecondary'
                      }`}
                    >
                      {isActive ? 'Added' : 'Available'}
                    </span>
                  </div>

                  <div className="mt-4 flex items-center justify-between text-[11px] text-textSecondary">
                    <span>
                      Min size {widget.minW}x{widget.minH}
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant={isActive ? 'outline' : 'default'}
                      onClick={() => (isActive ? removeWidget(widgetId) : addWidget(widgetId))}
                    >
                      {isActive ? 'Remove' : 'Add'}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {availableWidgets.length === 0 && (
            <div className="rounded-lg border border-dashed border-borderDefault px-4 py-8 text-center text-sm text-textSecondary">
              No widgets match that search.
            </div>
          )}
        </div>
      </Modal>

      <Modal
        open={isSavePresetOpen}
        onClose={closeSavePresetModal}
        title="Save Dashboard Preset"
        maxWidthClass="max-w-lg"
      >
        <form onSubmit={handleCreatePreset} className="space-y-4">
          <div>
            <label htmlFor="dashboard-preset-name" className="mb-1 block text-xs font-medium text-textSecondary">
              Preset name
            </label>
            <input
              id="dashboard-preset-name"
              type="text"
              value={presetName}
              onChange={(event) => setPresetName(event.target.value)}
              placeholder="Morning workflow"
              className="w-full rounded-lg border border-borderDefault bg-backgroundBody px-3 py-2 text-sm text-textPrimary outline-none transition focus:border-primary"
              maxLength={255}
              autoFocus
            />
          </div>

          {isStaff && (
            <label className="flex items-start gap-3 rounded-lg border border-borderDefault bg-backgroundBody/40 px-3 py-3">
              <input
                type="checkbox"
                checked={saveAsGlobal}
                onChange={(event) => setSaveAsGlobal(event.target.checked)}
                className="mt-1 h-4 w-4 rounded border-borderDefault"
              />
              <div>
                <div className="text-sm font-medium text-textPrimary">Make this preset global</div>
                <div className="mt-1 text-xs text-textSecondary">Global presets are available to all users and can only be edited by staff.</div>
              </div>
            </label>
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={closeSavePresetModal} disabled={presetBusy}>
              Cancel
            </Button>
            <Button type="submit" disabled={presetBusy || !presetName.trim()}>
              {presetBusy ? 'Saving…' : 'Save Preset'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default DashboardWidgetCanvas;
